/**
 * Admin > Recording
 *
 * Three inline sections, plus three worker-run buttons up top:
 *   - Buckets       (Azure Blob enrollment + test connection)
 *   - Retention     (global default + per-queue overrides)
 *   - Archive Rules (criteria → bucket)
 *
 * Worker buttons hit `/api/admin/recording/{ingest|archive|retention}/run`.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { recordingAdminApi, queuesApi } from '../../../api/client';
import HelpButton from '../../help/HelpButton';

export default function RecordingPage() {
  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Recording</h1>
        <HelpButton moduleKey="admin_recording" />
      </div>
      <WorkerBar />
      <BucketsSection />
      <RetentionSection />
      <ArchiveRulesSection />
    </div>
  );
}


// =====================================================================
// Worker bar
// =====================================================================


function WorkerBar() {
  const [status, setStatus] = useState({});
  const run = async (kind) => {
    setStatus({ ...status, [kind]: '…' });
    try {
      const fn = kind === 'ingest' ? recordingAdminApi.runIngest
              : kind === 'archive' ? recordingAdminApi.runArchive
              : kind === 'enrich' ? recordingAdminApi.runEnrich
              : recordingAdminApi.runRetention;
      const r = await fn();
      setStatus({ ...status, [kind]: r.summary });
    } catch (e) {
      setStatus({ ...status, [kind]: { error: e.detail || e.message } });
    }
  };
  return (
    <section className="border border-surface-border dark:border-surface-dark-border rounded-thin p-4 bg-surface-subtle/30 dark:bg-surface-dark-subtle/30">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-base font-semibold">Workers</h2>
        <p className="text-xs text-surface-muted">Run on demand. (Cron-driven schedules coming later.)</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {['ingest', 'enrich', 'archive', 'retention'].map((k) => (
          <div key={k} className="border border-surface-border dark:border-surface-dark-border rounded-thin p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium capitalize">{k}</div>
                <div className="text-xs text-surface-muted">
                  {k === 'ingest'    && 'Scan Asterisk monitor dirs, pull new WAVs into the index.'}
                  {k === 'enrich'    && 'Join Issabel CDR by uniqueid to fill caller / agent / duration.'}
                  {k === 'archive'   && 'Apply archive rules, upload matching recordings to bucket.'}
                  {k === 'retention' && 'Evict local recordings past their retention horizon.'}
                </div>
              </div>
              <button onClick={() => run(k)} className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 text-white text-xs">Run</button>
            </div>
            {status[k] && (
              <pre className="mt-2 text-xs bg-surface-base dark:bg-surface-dark-base p-2 rounded-thin overflow-x-auto">
                {typeof status[k] === 'string' ? status[k] : JSON.stringify(status[k], null, 2)}
              </pre>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}


// =====================================================================
// Buckets
// =====================================================================


function BucketsSection() {
  const qc = useQueryClient();
  const { data: buckets = [], isLoading } = useQuery({
    queryKey: ['admin', 'buckets'],
    queryFn: () => recordingAdminApi.bucketsList(),
  });
  const [editing, setEditing] = useState(null);

  const testIt = useMutation({
    mutationFn: (id) => recordingAdminApi.bucketTest(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'buckets'] }),
  });
  const remove = useMutation({
    mutationFn: (id) => recordingAdminApi.bucketDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'buckets'] }),
  });

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold">Storage buckets</h2>
          <p className="text-xs text-surface-muted">Archive destinations (Azure Blob today; S3 / GCS coming).</p>
        </div>
        <button onClick={() => setEditing({ __new: true })} className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 text-white text-sm">+ New bucket</button>
      </div>
      {isLoading ? (
        <div className="text-sm text-surface-muted">Loading…</div>
      ) : (
        <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-left font-medium">Kind</th>
                <th className="px-3 py-2 text-left font-medium">Account / Container</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Last test</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {buckets.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-surface-muted">No buckets yet.</td></tr>
              )}
              {buckets.map((b) => (
                <tr key={b.id} className="border-t border-surface-border dark:border-surface-dark-border">
                  <td className="px-3 py-2 font-medium">{b.name}</td>
                  <td className="px-3 py-2">{b.kind}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {b.config_hint?.account_name}/{b.config_hint?.container}
                    <div className="text-surface-muted">prefix: {b.config_hint?.prefix || '—'} · auth: {b.config_hint?.auth_kind} · ****{b.config_hint?.secret_last4 || '????'}</div>
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={b.status} />
                    {b.status_message && <div className="text-xs text-surface-muted mt-1 max-w-xs truncate" title={b.status_message}>{b.status_message}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">{b.last_test_at ? new Date(b.last_test_at).toLocaleString() : '—'}</td>
                  <td className="px-3 py-2 text-right space-x-3">
                    <button onClick={() => testIt.mutate(b.id)} disabled={testIt.isPending} className="text-amber-600 text-sm">Test</button>
                    <button onClick={() => setEditing(b)} className="text-continuum-600 dark:text-continuum-200 text-sm">Edit</button>
                    <button onClick={() => { if (window.confirm(`Delete bucket "${b.name}"?`)) remove.mutate(b.id); }} className="text-red-600 text-sm">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <BucketEditModal bucket={editing.__new ? null : editing} onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['admin', 'buckets'] }); }} />}
    </section>
  );
}


function StatusPill({ status }) {
  const cls = {
    ok:       'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    error:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    untested: 'bg-surface-subtle text-surface-muted dark:bg-surface-dark-subtle dark:text-surface-dark-muted',
    disabled: 'bg-surface-subtle text-surface-muted dark:bg-surface-dark-subtle',
  }[status] || 'bg-surface-subtle';
  return <span className={'px-2 py-0.5 rounded-thin text-xs ' + cls}>{status}</span>;
}


function BucketEditModal({ bucket, onClose }) {
  const isEdit = !!bucket;
  const [name, setName] = useState(bucket?.name || '');
  const [acct, setAcct] = useState(bucket?.config_hint?.account_name || '');
  const [container, setContainer] = useState(bucket?.config_hint?.container || '');
  const [authKind, setAuthKind] = useState('account_key');
  const [secret, setSecret] = useState('');
  const [prefix, setPrefix] = useState(bucket?.config_hint?.prefix || '');
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: () => {
      const azure = {
        account_name: acct.trim(),
        container: container.trim(),
        prefix: prefix.trim(),
      };
      if (secret) {
        if (authKind === 'account_key') azure.account_key = secret;
        else azure.sas_token = secret;
      }
      const payload = { name: name.trim(), kind: 'azure_blob', azure_config: azure };
      return isEdit
        ? recordingAdminApi.bucketUpdate(bucket.id, { name: payload.name, azure_config: secret ? azure : undefined })
        : recordingAdminApi.bucketCreate(payload);
    },
    onSuccess: onClose,
    onError: (e) => setError(e.detail || e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-lg bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
          <h3 className="font-semibold">{isEdit ? `Edit bucket: ${bucket.name}` : 'New Azure Blob bucket'}</h3>
          <button onClick={onClose} className="text-surface-muted">✕</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <Labeled label="Name (display only)"><Input v={name} onV={setName} /></Labeled>
          <Labeled label="Azure storage account name">
            <Input v={acct} onV={setAcct} placeholder="e.g. continuumcxstorage" />
          </Labeled>
          <Labeled label="Container">
            <Input v={container} onV={setContainer} placeholder="e.g. recordings" />
          </Labeled>
          <Labeled label="Prefix (optional, e.g. 'archive/')">
            <Input v={prefix} onV={setPrefix} placeholder="e.g. archive/" />
          </Labeled>
          <Labeled label="Auth">
            <select value={authKind} onChange={(e) => setAuthKind(e.target.value)} className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin">
              <option value="account_key">Account key</option>
              <option value="sas_token">SAS token</option>
            </select>
          </Labeled>
          <Labeled label={isEdit ? `Replace ${authKind === 'account_key' ? 'account key' : 'SAS token'} (leave empty to keep current)` : (authKind === 'account_key' ? 'Account key' : 'SAS token')}>
            <Input v={secret} onV={setSecret} type="password" placeholder={isEdit ? '••• keep current •••' : ''} />
          </Labeled>
          {error && <div className="text-red-600 text-sm">{String(error)}</div>}
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">Cancel</button>
          <button
            disabled={!name.trim() || !acct.trim() || !container.trim() || (!isEdit && !secret) || save.isPending}
            onClick={() => save.mutate()}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 disabled:opacity-40 text-white text-sm"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


// =====================================================================
// Retention
// =====================================================================


function RetentionSection() {
  const qc = useQueryClient();
  const { data: policies = [] } = useQuery({
    queryKey: ['admin', 'retention'],
    queryFn: () => recordingAdminApi.retentionList(),
  });
  const { data: queues = [] } = useQuery({
    queryKey: ['admin', 'queues'],
    queryFn: () => queuesApi.list(),
  });
  const [editing, setEditing] = useState(null);
  const remove = useMutation({
    mutationFn: (id) => recordingAdminApi.retentionDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'retention'] }),
  });

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold">Retention policies</h2>
          <p className="text-xs text-surface-muted">Days to keep recordings on local disk before they're archived or deleted.</p>
        </div>
        <button onClick={() => setEditing({ __new: true })} className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 text-white text-sm">+ Per-queue override</button>
      </div>
      <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Scope</th>
              <th className="px-3 py-2 text-left font-medium">Queue</th>
              <th className="px-3 py-2 text-left font-medium">Days to keep</th>
              <th className="px-3 py-2 text-left font-medium">Max size (GB)</th>
              <th className="px-3 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.id} className="border-t border-surface-border dark:border-surface-dark-border">
                <td className="px-3 py-2 capitalize">{p.scope}</td>
                <td className="px-3 py-2">{p.queue_id || '—'}</td>
                <td className="px-3 py-2">{p.days_to_keep}</td>
                <td className="px-3 py-2">{p.max_size_gb || '—'}</td>
                <td className="px-3 py-2 text-right space-x-3">
                  <button onClick={() => setEditing(p)} className="text-continuum-600 dark:text-continuum-200 text-sm">Edit</button>
                  {p.scope !== 'global' && (
                    <button onClick={() => { if (window.confirm('Delete this override?')) remove.mutate(p.id); }} className="text-red-600 text-sm">Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <RetentionEditModal policy={editing.__new ? null : editing} queues={queues} onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['admin', 'retention'] }); }} />}
    </section>
  );
}


function RetentionEditModal({ policy, queues, onClose }) {
  const isEdit = !!policy;
  const [scope, setScope] = useState(policy?.scope || 'queue');
  const [queueId, setQueueId] = useState(policy?.queue_id || '');
  const [days, setDays] = useState(policy?.days_to_keep ?? 30);
  const [maxGb, setMaxGb] = useState(policy?.max_size_gb || 0);
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: () => {
      const payload = { scope, queue_id: scope === 'global' ? null : queueId, days_to_keep: Number(days), max_size_gb: Number(maxGb) };
      return isEdit ? recordingAdminApi.retentionUpdate(policy.id, payload) : recordingAdminApi.retentionCreate(payload);
    },
    onSuccess: onClose,
    onError: (e) => setError(e.detail || e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
          <h3 className="font-semibold">{isEdit ? 'Edit retention' : 'New retention override'}</h3>
          <button onClick={onClose} className="text-surface-muted">✕</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          {!isEdit && (
            <Labeled label="Scope">
              <select value={scope} onChange={(e) => setScope(e.target.value)} className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin">
                <option value="queue">Per-queue override</option>
                <option value="global">Global default (one per system)</option>
              </select>
            </Labeled>
          )}
          {scope === 'queue' && (
            <Labeled label="Queue">
              <select value={queueId} onChange={(e) => setQueueId(e.target.value)} className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin">
                <option value="">— select queue —</option>
                {queues.map((q) => <option key={q.id || q.queue_id} value={q.queue_id}>{q.queue_id} · {q.name}</option>)}
              </select>
            </Labeled>
          )}
          <Labeled label="Days to keep"><Input v={days} onV={setDays} type="number" /></Labeled>
          <Labeled label="Max total size (GB, 0 = unlimited)"><Input v={maxGb} onV={setMaxGb} type="number" /></Labeled>
          {error && <div className="text-red-600 text-sm">{String(error)}</div>}
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">Cancel</button>
          <button disabled={save.isPending} onClick={() => save.mutate()} className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 disabled:opacity-40 text-white text-sm">
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


// =====================================================================
// Archive Rules
// =====================================================================


function ArchiveRulesSection() {
  const qc = useQueryClient();
  const { data: rules = [] } = useQuery({
    queryKey: ['admin', 'archive-rules'],
    queryFn: () => recordingAdminApi.rulesList(),
  });
  const { data: buckets = [] } = useQuery({
    queryKey: ['admin', 'buckets'],
    queryFn: () => recordingAdminApi.bucketsList(),
  });
  const [editing, setEditing] = useState(null);
  const remove = useMutation({
    mutationFn: (id) => recordingAdminApi.ruleDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'archive-rules'] }),
  });
  const bucketName = (id) => buckets.find((b) => b.id === id)?.name || '(missing)';

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold">Archive rules</h2>
          <p className="text-xs text-surface-muted">Priority-ordered. First matching rule wins; the recording is uploaded to that rule's bucket.</p>
        </div>
        <button
          disabled={buckets.length === 0}
          onClick={() => setEditing({ __new: true })}
          className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 disabled:opacity-40 text-white text-sm"
          title={buckets.length === 0 ? 'Add a bucket first' : ''}
        >
          + New rule
        </button>
      </div>
      <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Pri</th>
              <th className="px-3 py-2 text-left font-medium">Name</th>
              <th className="px-3 py-2 text-left font-medium">Criteria</th>
              <th className="px-3 py-2 text-left font-medium">Bucket</th>
              <th className="px-3 py-2 text-left font-medium">Delete local</th>
              <th className="px-3 py-2 text-left font-medium">Enabled</th>
              <th className="px-3 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-surface-muted">No archive rules.</td></tr>
            )}
            {rules.map((r) => (
              <tr key={r.id} className="border-t border-surface-border dark:border-surface-dark-border">
                <td className="px-3 py-2 font-mono">{r.priority}</td>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{Object.keys(r.criteria || {}).length === 0 ? '— never —' : JSON.stringify(r.criteria)}</td>
                <td className="px-3 py-2">{bucketName(r.bucket_id)}</td>
                <td className="px-3 py-2">{r.delete_local_after_archive ? '✓' : '—'}</td>
                <td className="px-3 py-2">{r.enabled ? '✓' : '—'}</td>
                <td className="px-3 py-2 text-right space-x-3">
                  <button onClick={() => setEditing(r)} className="text-continuum-600 dark:text-continuum-200 text-sm">Edit</button>
                  <button onClick={() => { if (window.confirm(`Delete rule "${r.name}"?`)) remove.mutate(r.id); }} className="text-red-600 text-sm">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <RuleEditModal rule={editing.__new ? null : editing} buckets={buckets} onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['admin', 'archive-rules'] }); }} />}
    </section>
  );
}


function RuleEditModal({ rule, buckets, onClose }) {
  const isEdit = !!rule;
  const [name, setName] = useState(rule?.name || '');
  const [priority, setPriority] = useState(rule?.priority ?? 100);
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [bucketId, setBucketId] = useState(rule?.bucket_id || buckets[0]?.id || '');
  const [deleteLocal, setDeleteLocal] = useState(rule?.delete_local_after_archive ?? true);
  const c0 = rule?.criteria || {};
  const [minAge, setMinAge] = useState(c0.min_age_days ?? '');
  const [queueIds, setQueueIds] = useState((c0.queue_ids || []).join(','));
  const [extensions, setExtensions] = useState((c0.agent_extensions || []).join(','));
  const [direction, setDirection] = useState(c0.direction || '');
  const [minDur, setMinDur] = useState(c0.min_duration_sec ?? '');
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: () => {
      const criteria = {};
      if (minAge !== '') criteria.min_age_days = Number(minAge);
      if (queueIds.trim()) criteria.queue_ids = queueIds.split(',').map((s) => s.trim()).filter(Boolean);
      if (extensions.trim()) criteria.agent_extensions = extensions.split(',').map((s) => s.trim()).filter(Boolean);
      if (direction) criteria.direction = direction;
      if (minDur !== '') criteria.min_duration_sec = Number(minDur);
      const payload = {
        name: name.trim(),
        priority: Number(priority),
        enabled,
        criteria,
        bucket_id: bucketId,
        delete_local_after_archive: deleteLocal,
      };
      return isEdit ? recordingAdminApi.ruleUpdate(rule.id, payload) : recordingAdminApi.ruleCreate(payload);
    },
    onSuccess: onClose,
    onError: (e) => setError(e.detail || e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-xl bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
          <h3 className="font-semibold">{isEdit ? `Edit rule: ${rule.name}` : 'New archive rule'}</h3>
          <button onClick={onClose} className="text-surface-muted">✕</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Name"><Input v={name} onV={setName} /></Labeled>
            <Labeled label="Priority (lower = first)"><Input v={priority} onV={setPriority} type="number" /></Labeled>
          </div>
          <Labeled label="Destination bucket">
            <select value={bucketId} onChange={(e) => setBucketId(e.target.value)} className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin">
              <option value="">— select —</option>
              {buckets.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.kind})</option>)}
            </select>
          </Labeled>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>Enabled</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={deleteLocal} onChange={(e) => setDeleteLocal(e.target.checked)} />
              <span>Delete local after archive</span>
            </label>
          </div>

          <div className="border-t border-surface-border dark:border-surface-dark-border pt-3 mt-3">
            <div className="text-xs uppercase tracking-wider text-surface-muted mb-2">Criteria (AND'd)</div>
            <Labeled label="Min age (days)"><Input v={minAge} onV={setMinAge} type="number" placeholder="e.g. 14" /></Labeled>
            <Labeled label="Queue IDs (comma-separated)"><Input v={queueIds} onV={setQueueIds} placeholder="60100,60101" /></Labeled>
            <Labeled label="Agent extensions (comma-separated)"><Input v={extensions} onV={setExtensions} placeholder="60001,60002" /></Labeled>
            <Labeled label="Direction">
              <select value={direction} onChange={(e) => setDirection(e.target.value)} className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin">
                <option value="">— any —</option>
                <option value="inbound">inbound</option>
                <option value="outbound">outbound</option>
                <option value="internal">internal</option>
              </select>
            </Labeled>
            <Labeled label="Min duration (sec)"><Input v={minDur} onV={setMinDur} type="number" placeholder="e.g. 30" /></Labeled>
          </div>

          {error && <div className="text-red-600 text-sm">{String(error)}</div>}
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">Cancel</button>
          <button
            disabled={!name.trim() || !bucketId || save.isPending}
            onClick={() => save.mutate()}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 disabled:opacity-40 text-white text-sm"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


// =====================================================================
// Tiny shared inputs
// =====================================================================


function Labeled({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-surface-muted dark:text-surface-dark-muted mb-1">{label}</label>
      {children}
    </div>
  );
}

function Input({ v, onV, type = 'text', placeholder }) {
  return (
    <input
      type={type}
      value={v}
      placeholder={placeholder}
      onChange={(e) => onV(e.target.value)}
      className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin text-sm"
    />
  );
}
